import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PrismicRichText, SliceZone } from "@prismicio/react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "components/Form/Button";
import { Layout } from "components/Layout";
import { ConditionalDataModal } from "components/ConditionalDataModal";
import { taskInstructionSliceComponents } from "components/TaskInstructionSlices";
import {
  CollectionDetailsContext,
  getCollectionDetailLabels,
} from "contexts/CollectionDetails";
import { CollectionDetailsForm } from "contexts/CollectionDetails/form";
import { captureDB } from "lib/indexed-db";
import { getAdditionalDataRequiredFields } from "lib/prismic";
import {
  getCollectionDetailsSchemaFingerprint,
  getStepRouteState,
  getStepSchemaFingerprint,
  getTaskStepDetailFields,
  getTaskSteps,
} from "lib/task-steps";
import { apiGetUserProfile, apiUpdateUserProfile } from "lib/api";
import { AppRoutes, CaptureType, isQrCaptureType } from "types";
import {
  hasChangedTaskPositions,
  getStoredWorkTaskSharedDetails,
  getWorkTaskStepRoute,
  getWorkTaskStepRouteState,
  isWorkTaskStepComplete,
} from "lib/work-task-navigation";
import { useTaskDefinition } from "hooks/useTaskDefinition";
import { useMultiStepCapture } from "contexts/MultiStepCaptureContext";
import {
  createTaskNavigationQr,
  getTaskNavigationSteps,
  getTaskNavigationTask,
  readTaskNavigationQr,
  TaskNavigationTarget,
} from "lib/task-navigation-qr";

type AdditionalDataStatus = "checking" | "satisfied" | "missing";

export function TaskInstructions() {
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [additionalDataStatus, setAdditionalDataStatus] =
    useState<AdditionalDataStatus>("checking");
  const [showConditionalModal, setShowConditionalModal] = useState(false);
  const [missingField, setMissingField] = useState("");
  const [conditionalModalLoading, setConditionalModalLoading] = useState(false);
  const [resumeNeedsCollectionDetails, setResumeNeedsCollectionDetails] =
    useState(false);
  const autoNavigationStarted = useRef(false);
  const [navigationError, setNavigationError] = useState<string>();
  const [navigationComplete, setNavigationComplete] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { config, contributor, markCollectionDetailsCompleted } = useContext(
    CollectionDetailsContext
  );
  const { invalidateCurrentTask } = useMultiStepCapture();

  const taskId = location.state?.taskId as string | undefined;
  const taskName = location.state?.taskName as string | undefined;
  const requestedTarget = location.state?.taskNavigation as
    | TaskNavigationTarget
    | undefined;
  const navigation = useMemo(() => {
    if (!requestedTarget || !config) return {};
    try {
      const target = readTaskNavigationQr(
        createTaskNavigationQr(requestedTarget),
        contributor
      );
      if (target.task_id !== taskId)
        throw new Error("The station QR does not match this task.");
      getTaskNavigationTask(target, config, contributor);
      return { target };
    } catch (problem) {
      return {
        error:
          problem instanceof Error
            ? problem.message
            : "This station could not be opened.",
      };
    }
  }, [config, contributor, requestedTarget, taskId]);
  const nextRoute = location.state?.nextRoute || AppRoutes.capture;
  const captureType = location.state?.captureType as string | undefined;
  const skipToProgress = Boolean(location.state?.skipToProgress);
  const editCollectionDetails = Boolean(location.state?.editCollectionDetails);
  const selectedStepNumber = Number(location.state?.stepNumber) || 1;
  const navigationEntryId = location.state?.navigationEntryId as
    | string
    | undefined;
  const workTaskDocumentId = taskId
    ? config?.tasks[taskId]?.workTaskDocumentId
    : undefined;
  const isWorkTask = Boolean(workTaskDocumentId);
  const {
    instructions,
    workTask: configuredWorkTask,
    orbTask,
    loading,
    error: taskLoadError,
  } = useTaskDefinition({
    taskId,
    workTaskDocumentId,
    enabled: config !== undefined,
    reloadKey: loadAttempt,
  });
  const workTask = configuredWorkTask || orbTask;
  // Missing fields keep the existing introduction screen for older documents.
  const showTaskIntro = workTask
    ? workTask.showTaskIntro !== false
    : instructions?.show_task_intro !== false;
  const loadError = taskLoadError
    ? taskLoadError instanceof Error &&
      taskLoadError.name === "LabelConfigurationError"
      ? taskLoadError.message
      : "We could not load this task. Your saved progress has not changed."
    : navigation.error || navigationError || null;

  const generalSlices = useMemo(
    () =>
      instructions?.instruction_content?.filter(
        (slice) => slice.slice_type === "instruction_text___image"
      ) || [],
    [instructions]
  );
  const steps = useMemo(
    () => workTask?.steps || getTaskSteps(instructions),
    [instructions, workTask]
  );
  const workTaskLabels = useMemo(() => {
    if (!workTask || !config?.labels) return undefined;

    return {
      devices: config.labels.devices,
      ...getCollectionDetailLabels(workTask.sharedCollectionDetailFields),
    } as typeof config.labels;
  }, [config, workTask]);
  const hasCollectionDetails = isWorkTask
    ? Boolean(
        workTask?.sharedCollectionDetailFields.length ||
          workTaskLabels?.devices.length
      )
    : Boolean(
        config?.labels &&
          Object.keys(config.labels).some((key) => key !== "devices")
      );
  const collectionDetailsSchemaFingerprint = useMemo(
    () =>
      hasCollectionDetails
        ? getCollectionDetailsSchemaFingerprint(
            isWorkTask ? workTaskLabels : config?.labels
          )
        : undefined,
    [config?.labels, hasCollectionDetails, isWorkTask, workTaskLabels]
  );

  useEffect(() => {
    if (!taskId) {
      navigate(AppRoutes.home);
    }
  }, [navigate, taskId]);

  useEffect(() => {
    autoNavigationStarted.current = false;
    setResumeNeedsCollectionDetails(false);
    setAdditionalDataStatus("checking");
    setNavigationError(undefined);
    setNavigationComplete(false);
  }, [config, loadAttempt, requestedTarget, taskId, workTaskDocumentId]);

  const checkAdditionalDataRequirements = useCallback(async (): Promise<{
    satisfied: boolean;
    missingField?: string;
  }> => {
    try {
      const requiredFields = workTask
        ? workTask.requiredAdditionalDataFields
        : getAdditionalDataRequiredFields(instructions);
      if (requiredFields.length === 0) return { satisfied: true };

      const userProfile = await apiGetUserProfile();
      const missingRequiredField = requiredFields.find(
        (field) => !userProfile[field]
      );

      return missingRequiredField
        ? { satisfied: false, missingField: missingRequiredField }
        : { satisfied: true };
    } catch (error) {
      console.error("Error checking additional data requirements:", error);
      if (requestedTarget) {
        setNavigationError(
          "Required participant details could not be checked. Please retry before capturing."
        );
        return { satisfied: false };
      }
      // Preserve the existing behavior: profile lookup failure does not block the task.
      return { satisfied: true };
    }
  }, [instructions, requestedTarget, workTask]);

  useEffect(() => {
    if (loading || !taskId) return;

    let cancelled = false;
    setAdditionalDataStatus("checking");

    checkAdditionalDataRequirements().then((result) => {
      if (cancelled) return;

      if (result.satisfied) {
        setAdditionalDataStatus("satisfied");
        return;
      }
      if (!result.missingField) return;

      setAdditionalDataStatus("missing");
      setMissingField(result.missingField || "");
      setShowConditionalModal(true);
    });

    return () => {
      cancelled = true;
    };
  }, [checkAdditionalDataRequirements, loading, taskId]);

  const ensureAdditionalDataSatisfied = useCallback(async () => {
    if (additionalDataStatus === "satisfied") return true;
    if (additionalDataStatus === "missing") return false;

    const result = await checkAdditionalDataRequirements();
    if (result.satisfied) {
      setAdditionalDataStatus("satisfied");
      return true;
    }

    setAdditionalDataStatus("missing");
    setMissingField(result.missingField || "");
    setShowConditionalModal(true);
    return false;
  }, [additionalDataStatus, checkAdditionalDataRequirements]);

  const navigateToProgress = useCallback(
    async (replace = false) => {
      if (!taskId || steps.length === 0) {
        navigate(nextRoute, {
          replace,
          state: { taskId, taskName, captureType },
        });
        return;
      }

      let storedCaptures: Awaited<
        ReturnType<typeof captureDB.getStepCaptures>
      > = [];
      try {
        storedCaptures = await captureDB.getStepCaptures(taskId);
      } catch (error) {
        console.error("Failed to load saved task progress:", error);
        if (requestedTarget) {
          setNavigationError(
            "Saved progress could not be loaded. Please retry before capturing."
          );
          return;
        }
        toast.error("Saved progress could not be loaded. Starting at step 1.");
      }

      if (
        navigation.target &&
        workTask &&
        hasChangedTaskPositions(workTask, storedCaptures)
      ) {
        setNavigationError(
          "Task positions changed since your progress was saved. Contact the collection manager before continuing. Your progress is preserved."
        );
        return;
      }

      if (
        skipToProgress &&
        collectionDetailsSchemaFingerprint &&
        storedCaptures.find((capture) => capture.stepNumber === 0)
          ?.schemaFingerprint !== collectionDetailsSchemaFingerprint
      ) {
        setResumeNeedsCollectionDetails(true);
        return;
      }

      if (workTask) {
        let storedStepDetails: Awaited<
          ReturnType<typeof captureDB.getStepDetails>
        > = [];
        try {
          storedStepDetails = await captureDB.getStepDetails(taskId);
        } catch (error) {
          console.error("Failed to load saved work-task step details:", error);
          if (requestedTarget) {
            setNavigationError(
              "Saved task details could not be loaded. Please retry before capturing."
            );
            return;
          }
          toast.error(
            "Saved step details could not be loaded. Starting at step 1."
          );
        }

        if (
          navigation.target &&
          hasChangedTaskPositions(workTask, storedStepDetails)
        ) {
          setNavigationError(
            "Task positions changed since your details were saved. Contact the collection manager before continuing. Your progress is preserved."
          );
          return;
        }
        const sharedDetails = getStoredWorkTaskSharedDetails(
          workTask,
          storedCaptures
        );
        let targetSteps = workTask.steps;
        if (navigation.target) {
          try {
            targetSteps = getTaskNavigationSteps(
              workTask,
              navigation.target.entry_id
            );
          } catch (problem) {
            setNavigationError(
              problem instanceof Error
                ? problem.message
                : "This task position is unavailable."
            );
            return;
          }
        }
        const nextStep = targetSteps.find(
          (step) =>
            !isWorkTaskStepComplete(
              step,
              storedCaptures,
              storedStepDetails,
              sharedDetails
            )
        );

        if (!nextStep) {
          if (navigation.target) {
            setNavigationComplete(true);
            return;
          }
          if (workTask.hasUploadSteps) {
            navigate(AppRoutes.multiStepUpload, {
              replace,
              state: { taskId, taskName: workTask.workTaskName },
            });
          } else {
            const lastStep = workTask.steps[workTask.steps.length - 1];
            navigate(getWorkTaskStepRoute(lastStep), {
              replace,
              state: getWorkTaskStepRouteState(workTask, lastStep),
            });
          }
          return;
        }

        navigate(getWorkTaskStepRoute(nextStep), {
          replace,
          state: getWorkTaskStepRouteState(
            workTask,
            nextStep,
            false,
            navigation.target?.entry_id
          ),
        });
        return;
      }

      const nextStep = steps.find((step) => {
        const capture = storedCaptures.find(
          (item) => item.stepNumber === step.stepNumber
        );
        if (!capture) return true;
        return !Boolean(
          capture.schemaFingerprint ===
            getStepSchemaFingerprint({
              captureType: captureType as CaptureType | undefined,
              numberOfCaptures: step.numberOfCaptures,
              fields: getTaskStepDetailFields(instructions, step.stepNumber),
              stepLabel: step.configuredStepLabel,
            }) &&
            (capture.isComplete ??
              capture.files.length >= capture.numberOfCaptures)
        );
      });

      if (!nextStep) {
        if (navigation.target) {
          setNavigationComplete(true);
          return;
        }
        navigate(AppRoutes.multiStepUpload, {
          replace,
          state: { taskId, taskName, captureType },
        });
        return;
      }

      navigate(AppRoutes.stepCapture, {
        replace,
        state: getStepRouteState(nextStep, steps.length, {
          taskId,
          taskName,
          captureType,
        }),
      });
    },
    [
      captureType,
      collectionDetailsSchemaFingerprint,
      instructions,
      navigate,
      navigation.target,
      nextRoute,
      steps,
      skipToProgress,
      requestedTarget,
      taskId,
      taskName,
      workTask,
    ]
  );

  // Keep setup on this route, but omit the extra Start/Continue click when
  // the introduction is disabled and no collection details need to be entered.
  const automaticallyContinue =
    !navigationComplete &&
    !editCollectionDetails &&
    !resumeNeedsCollectionDetails &&
    ((skipToProgress && steps.length > 0) ||
      (!showTaskIntro && !hasCollectionDetails));

  useEffect(() => {
    if (
      !automaticallyContinue ||
      !taskId ||
      config === undefined ||
      loadError ||
      loading ||
      additionalDataStatus !== "satisfied" ||
      autoNavigationStarted.current
    ) {
      return;
    }

    autoNavigationStarted.current = true;
    void navigateToProgress(true);
  }, [
    additionalDataStatus,
    automaticallyContinue,
    config,
    loadError,
    loading,
    navigateToProgress,
    taskId,
  ]);

  const handleContinueWithoutCollectionDetails = useCallback(async () => {
    if (!(await ensureAdditionalDataSatisfied())) return;
    await navigateToProgress();
  }, [ensureAdditionalDataSatisfied, navigateToProgress]);

  const handleContinueFromCollectionDetails = useCallback(async () => {
    if (!(await ensureAdditionalDataSatisfied())) return;

    markCollectionDetailsCompleted();
    if (editCollectionDetails) {
      // Step zero was saved directly to IndexedDB. Invalidate the matching
      // in-memory task so final review restores the updated shared details.
      invalidateCurrentTask();
      navigate(nextRoute, {
        state: {
          taskId,
          taskName,
          captureType,
          stepNumber: selectedStepNumber,
          ...(navigationEntryId ? { navigationEntryId } : {}),
        },
      });
      return;
    }

    await navigateToProgress();
  }, [
    captureType,
    editCollectionDetails,
    ensureAdditionalDataSatisfied,
    invalidateCurrentTask,
    markCollectionDetailsCompleted,
    navigate,
    navigateToProgress,
    nextRoute,
    taskId,
    taskName,
    selectedStepNumber,
    navigationEntryId,
  ]);

  const handleBack = useCallback(() => {
    if (editCollectionDetails) {
      navigate(nextRoute, {
        state: {
          taskId,
          taskName,
          captureType,
          stepNumber: selectedStepNumber,
          ...(navigationEntryId ? { navigationEntryId } : {}),
        },
      });
      return;
    }
    navigate(AppRoutes.home);
  }, [
    captureType,
    editCollectionDetails,
    navigate,
    nextRoute,
    taskId,
    taskName,
    selectedStepNumber,
    navigationEntryId,
  ]);

  const handleConditionalDataSubmit = useCallback(
    async (data: Record<string, any>) => {
      setConditionalModalLoading(true);
      try {
        await apiUpdateUserProfile(data);
        setShowConditionalModal(false);
        setMissingField("");

        const result = await checkAdditionalDataRequirements();
        if (result.satisfied) {
          setAdditionalDataStatus("satisfied");
        } else {
          setMissingField(result.missingField || "");
          setShowConditionalModal(true);
          setAdditionalDataStatus("missing");
        }
      } catch (error) {
        console.error("Error updating user profile:", error);
        if (requestedTarget) {
          setNavigationError(
            "Required participant details could not be saved. Please retry before capturing."
          );
          setShowConditionalModal(false);
          return;
        }
        // Preserve the existing behavior for profile service failures.
        setAdditionalDataStatus("satisfied");
      } finally {
        setConditionalModalLoading(false);
      }
    },
    [checkAdditionalDataRequirements, requestedTarget]
  );

  const handleConditionalDataCancel = useCallback(() => {
    setShowConditionalModal(false);
    setMissingField("");
    navigate(AppRoutes.home);
  }, [navigate]);

  const taskIntroduction = configuredWorkTask ? (
    <div className="space-y-4 text-center">
      <h1 className="text-24 font-semibold">
        {configuredWorkTask.workTaskName}
      </h1>
      <div className="space-y-3 text-left text-191c20">
        <PrismicRichText field={configuredWorkTask.instructions} />
      </div>
    </div>
  ) : generalSlices.length > 0 ? (
    <SliceZone
      slices={generalSlices}
      components={taskInstructionSliceComponents}
    />
  ) : (
    <div className="space-y-4 text-center">
      <h1 className="text-24 font-semibold">
        {instructions?.task_name || taskName || "Task Instructions"}
      </h1>
      <p className="text-858494">Ready to start this task?</p>
    </div>
  );

  const conditionalDataModal = (
    <ConditionalDataModal
      isOpen={showConditionalModal}
      requiredField={missingField}
      onSubmit={handleConditionalDataSubmit}
      onCancel={handleConditionalDataCancel}
      loading={conditionalModalLoading}
    />
  );

  if (navigationComplete && !loadError) {
    let completedSteps = workTask?.steps || [];
    try {
      if (workTask && navigation.target?.entry_id)
        completedSteps = getTaskNavigationSteps(
          workTask,
          navigation.target.entry_id
        );
    } catch {
      completedSteps = [];
    }
    const firstQrStep = completedSteps.find((step) =>
      isQrCaptureType(step.captureType)
    );
    const hasSavedUploads = workTask
      ? completedSteps.some((step) => !isQrCaptureType(step.captureType))
      : !isQrCaptureType(captureType);
    return (
      <Layout className="space-y-6">
        <h1 className="text-24 font-semibold">
          {hasSavedUploads
            ? "Captures already saved"
            : "This task is already completed"}
        </h1>
        <p className="text-858494">
          {hasSavedUploads
            ? "Review and upload your saved captures before starting another round. Your files and progress have not been reset."
            : "You can open this task again to review or repeat a capture. Your saved progress has not been reset."}
        </p>
        {(workTask ? workTask.hasUploadSteps : hasSavedUploads) && (
          <Button
            onClick={() =>
              navigate(AppRoutes.multiStepUpload, {
                state: { taskId, taskName, captureType },
              })
            }
          >
            Review saved captures
          </Button>
        )}
        {workTask && firstQrStep && (
          <Button
            onClick={() => {
              navigate(getWorkTaskStepRoute(firstQrStep), {
                state: {
                  ...getWorkTaskStepRouteState(
                    workTask,
                    firstQrStep,
                    false,
                    navigation.target?.entry_id
                  ),
                  reviewCompletedSteps: true,
                },
              });
            }}
          >
            Review or repeat
          </Button>
        )}
        <Button onClick={() => navigate(AppRoutes.home)}>
          Back to campaign
        </Button>
      </Layout>
    );
  }

  if (
    loading ||
    config === undefined ||
    (automaticallyContinue && !loadError)
  ) {
    return (
      <Layout className="space-y-8">
        <div className="flex min-h-[200px] items-center justify-center">
          <p className="text-858494">Loading task setup...</p>
        </div>
        {conditionalDataModal}
      </Layout>
    );
  }

  if (loadError) {
    return (
      <Layout className="space-y-8">
        <div className="space-y-4 text-center">
          <h1 className="text-24 font-semibold">Instructions unavailable</h1>
          <p className="text-858494">{loadError}</p>
          <Button onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
            Try Again
          </Button>
          <button
            onClick={handleBack}
            className="text-sm text-858494 underline hover:text-191c20"
          >
            Back to Homepage
          </button>
        </div>
      </Layout>
    );
  }

  if (hasCollectionDetails && config) {
    return (
      <>
        <CollectionDetailsForm
          contributor={contributor}
          setIsShow={handleBack}
          onSubmit={handleContinueFromCollectionDetails}
          heading="Collection details"
          description="Add the details required for this task, then continue to the first incomplete step."
          continueLabel={editCollectionDetails ? "Save Changes" : "Start Task"}
          cancelLabel={
            editCollectionDetails ? "Back to Review" : "Back to Home"
          }
          labelsOverride={workTaskLabels}
          labelRelationsOverride={workTask?.labelRelations}
          additionalDataRequiredOverride={
            workTask?.requiredAdditionalDataFields
          }
          knownValuesRefreshKey={additionalDataStatus}
          schemaFingerprint={collectionDetailsSchemaFingerprint}
        >
          {showTaskIntro && (
            <section className="space-y-6" aria-label="Task instructions">
              {taskIntroduction}
            </section>
          )}
        </CollectionDetailsForm>
        {conditionalDataModal}
      </>
    );
  }

  return (
    <>
      <Layout
        className="space-y-8"
        toolbarClassName="!py-3"
        toolbar={
          <>
            <Button
              onClick={handleContinueWithoutCollectionDetails}
              disabled={additionalDataStatus === "checking"}
            >
              {steps.length > 0 ? "Start Task" : "Continue"}
            </Button>
            <button
              onClick={handleBack}
              className="text-sm text-858494 underline hover:text-191c20"
            >
              Back to Homepage
            </button>
          </>
        }
      >
        <section className="space-y-6" aria-label="Task instructions">
          {taskIntroduction}
        </section>
      </Layout>
      {conditionalDataModal}
    </>
  );
}
